import React from 'react';
import { useNavigate } from 'react-router-dom';

const AGENCY_URL = 'https://wiselydrivenai.com';
const DEMO_CARD_URL = '/demo';

const styles = {
  page: {
    backgroundColor: '#111827',
    minHeight: '100vh',
    fontFamily: "'Outfit', system-ui, sans-serif",
    color: '#F9FAFB',
  },
  serif: {
    fontFamily: "'DM Serif Display', Georgia, serif",
  },
  orange: { color: '#F97316' },
  nav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.25rem 2rem',
    borderBottom: '1px solid #1F2937',
    backdropFilter: 'blur(8px)',
    backgroundColor: 'rgba(17,24,39,0.85)',
    position: 'sticky',
    top: 0,
    zIndex: 50,
  },
  logoText: {
    fontFamily: "'DM Serif Display', Georgia, serif",
    fontSize: '1.25rem',
    color: '#F9FAFB',
    textDecoration: 'none',
  },
  navLink: {
    color: '#9CA3AF',
    textDecoration: 'none',
    fontSize: '0.875rem',
    transition: 'color 0.2s',
  },
  ctaBtn: {
    backgroundColor: '#F97316',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '9999px',
    padding: '0.625rem 1.5rem',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Outfit', system-ui, sans-serif",
    transition: 'background-color 0.2s',
  },
  hero: {
    padding: '6rem 2rem 5rem',
    textAlign: 'center',
    maxWidth: '860px',
    margin: '0 auto',
  },
  h1: {
    fontFamily: "'DM Serif Display', Georgia, serif",
    fontSize: 'clamp(2.5rem, 6vw, 4rem)',
    lineHeight: 1.15,
    marginBottom: '1.5rem',
    color: '#F9FAFB',
  },
  heroSub: {
    fontSize: '1.1rem',
    color: '#9CA3AF',
    maxWidth: '560px',
    margin: '0 auto 2.5rem',
    lineHeight: 1.7,
  },
  section: {
    padding: '4rem 2rem',
    maxWidth: '960px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontFamily: "'DM Serif Display', Georgia, serif",
    fontSize: '2rem',
    marginBottom: '1rem',
    color: '#F9FAFB',
    textAlign: 'center',
  },
  steps: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '2rem',
    marginTop: '2.5rem',
  },
  stepCard: {
    backgroundColor: '#1F2937',
    borderRadius: '1rem',
    padding: '1.75rem',
    border: '1px solid #374151',
  },
  stepNum: {
    width: '2.5rem',
    height: '2.5rem',
    borderRadius: '9999px',
    backgroundColor: 'rgba(249,115,22,0.15)',
    color: '#F97316',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem',
    fontSize: '1rem',
  },
  callout: {
    backgroundColor: '#1F2937',
    border: '1px solid #F97316',
    borderRadius: '1rem',
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '600px',
    margin: '0 auto',
  },
  footer: {
    borderTop: '1px solid #1F2937',
    padding: '2rem',
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '0.875rem',
  },
};

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <a href={AGENCY_URL} style={styles.logoText} target="_blank" rel="noopener noreferrer">
          Wisely Driven <span style={styles.orange}>Consulting</span>
        </a>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
          <a href={AGENCY_URL} style={styles.navLink} target="_blank" rel="noopener noreferrer">
            Agency
          </a>
          <button style={styles.ctaBtn} onClick={() => navigate('/get-my-card')}>
            Get My Free Card
          </button>
        </div>
      </nav>

      <section style={styles.hero}>
        <h1 style={styles.h1}>
          Your business card,{' '}
          <span style={styles.orange}>reimagined</span>
        </h1>
        <p style={styles.heroSub}>
          Get a beautiful digital business card with a QR code — free. Share it anywhere,
          save your contacts instantly. No app required.
        </p>
        <button
          style={{ ...styles.ctaBtn, fontSize: '1.05rem', padding: '0.875rem 2.25rem' }}
          onClick={() => navigate('/get-my-card')}
          onMouseEnter={e => e.target.style.backgroundColor = '#EA580C'}
          onMouseLeave={e => e.target.style.backgroundColor = '#F97316'}
        >
          Get My Free Card
        </button>
      </section>

      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>How it works</h2>
        <p style={{ textAlign: 'center', color: '#9CA3AF', marginBottom: '0' }}>
          From form to QR code in seconds.
        </p>
        <div style={styles.steps}>
          {[
            { num: '1', title: 'Fill your info', desc: 'Enter your name, contact details, social links, and a photo.' },
            { num: '2', title: 'Get your card', desc: 'Your digital card is created instantly — complete with a unique QR code.' },
            { num: '3', title: 'Share anywhere', desc: 'Share the link or QR code at events, on social media, or in emails.' },
          ].map(step => (
            <div key={step.num} style={styles.stepCard}>
              <div style={styles.stepNum}>{step.num}</div>
              <div style={{ fontWeight: '600', marginBottom: '0.5rem', fontSize: '1rem' }}>{step.title}</div>
              <div style={{ color: '#9CA3AF', fontSize: '0.9rem', lineHeight: 1.6 }}>{step.desc}</div>
            </div>
          ))}
        </div>
      </section>

      <section style={{ ...styles.section, textAlign: 'center' }}>
        <h2 style={styles.sectionTitle}>See what you'll get</h2>
        <p style={{ color: '#9CA3AF', marginBottom: '1.5rem' }}>
          Here's a live example of what your card will look like.
        </p>
        <a
          href={DEMO_CARD_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            backgroundColor: '#1F2937',
            color: '#F97316',
            padding: '0.75rem 1.75rem',
            borderRadius: '9999px',
            textDecoration: 'none',
            border: '1px solid #F97316',
            fontWeight: '600',
            fontSize: '0.9rem',
          }}
          onMouseEnter={e => e.target.style.backgroundColor = 'rgba(249,115,22,0.1)'}
          onMouseLeave={e => e.target.style.backgroundColor = '#1F2937'}
        >
          View Demo Card →
        </a>
      </section>

      <section style={{ ...styles.section }}>
        <div style={styles.callout}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🎨</div>
          <h3 style={{ ...styles.serif, fontSize: '1.5rem', marginBottom: '0.75rem', color: '#F9FAFB' }}>
            Have a website? We'll match your card.
          </h3>
          <p style={{ color: '#9CA3AF', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '1.25rem' }}>
            Just provide your URL and we'll extract your brand colors to make your card feel like
            a natural extension of your existing identity.
          </p>
          <p style={{ color: '#6B7280', fontSize: '0.8rem' }}>
            Don't have a website yet? We build those too — your card is just the beginning.
          </p>
        </div>
      </section>

      <section style={{ ...styles.section, textAlign: 'center', paddingTop: '2rem' }}>
        <p style={{ color: '#6B7280', fontSize: '0.875rem', maxWidth: '500px', margin: '0 auto', lineHeight: 1.7 }}>
          We'll keep your card free. In exchange, you may occasionally hear from Wisely Driven Consulting
          about digital services that could help your business. No spam — unsubscribe anytime.
        </p>
      </section>

      <section style={{ textAlign: 'center', padding: '3rem 2rem 5rem' }}>
        <button
          style={{ ...styles.ctaBtn, fontSize: '1.1rem', padding: '1rem 2.5rem' }}
          onClick={() => navigate('/get-my-card')}
          onMouseEnter={e => e.target.style.backgroundColor = '#EA580C'}
          onMouseLeave={e => e.target.style.backgroundColor = '#F97316'}
        >
          Get My Free Card
        </button>
      </section>

      <footer style={styles.footer}>
        <p>
          Powered by{' '}
          <a href={AGENCY_URL} style={{ color: '#F97316', textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
            Wisely Driven Consulting
          </a>
        </p>
      </footer>
    </div>
  );
}
